import { BackLink } from '@mdvip/shared/components/blocks/BackLink';
import type { Facet, FacetValue } from '@mdvip/shared/components/blocks/ContentFilters';
import { ContentListing, type ContentListingChip } from '@mdvip/shared/components/blocks/ContentListing';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Checkbox } from '@mdvip/shared/components/ui/checkbox';
import { Label } from '@mdvip/shared/components/ui/label';
import { Switch } from '@mdvip/shared/components/ui/switch';
import { useDebouncedValue } from '@mdvip/shared/hooks/useDebouncedValue';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toRecipeCard } from './recipeCard.mapper';
import { RECIPE_FACET_GROUPS, RECIPE_FACET_ORDER } from './recipeFacetGroups';
import { useRecipeFacets } from './useRecipeFacets';
import { useRecipes } from './useRecipes';
import { useToggleRecipeBookmark } from './useToggleRecipeBookmark';

const PAGE_SIZE = 9;
const PAGE_PARAM = 'page';
const SEARCH_PARAM = 'search';
const SAVED_PARAM = 'saved';
const SEARCH_DEBOUNCE_MS = 300;

/** camelCase URL/query field ids for all 9 taxonomies, in the canonical accordion display order. */
const FIELD_IDS = RECIPE_FACET_ORDER.map((vocabulary) => RECIPE_FACET_GROUPS[vocabulary].fieldId);

function parsePage(raw: string | null): number {
  const page = Number(raw);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

/** Reads the 9 taxonomy filters straight from the URL — repeated `?cuisine=A&cuisine=B` params, one per active term. */
function parseFilters(searchParams: URLSearchParams): FacetValue {
  const result: FacetValue = {};
  for (const fieldId of FIELD_IDS) {
    const values = searchParams.getAll(fieldId);
    if (values.length) result[fieldId] = values;
  }
  return result;
}

/**
 * The Recipes browsing page — a paginated, filterable grid of licensed EatingWell recipe cards.
 * `page`, the 9 taxonomy filters, and the search term are all driven by the URL (not component
 * state), so browser back/forward, a hard reload, and a deep link from the recipe-detail page's
 * tag pills (`?<field>=<value>`) all land on the same view.
 *
 * No built-in error slot on `ContentListing` (a deliberately filter/error-free base), so a failed
 * `recipes` fetch swaps the whole listing for an inline Alert + retry instead of rendering the
 * grid. A failed `recipeFacets` fetch is not surfaced the same way — the filter panel just
 * degrades to empty accordions, since losing facet counts isn't worth blocking the page.
 */
export function RecipesPage() {
  const { t } = useTranslation('shared');
  const tError = useErrorTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePage(searchParams.get(PAGE_PARAM));
  const search = searchParams.get(SEARCH_PARAM) ?? '';
  const savedOnly = searchParams.get(SAVED_PARAM) === '1';
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // Syncs the visible input from the URL when it changes from outside typing (Clear all, browser
  // back/forward, a deep link). Once the debounced commit below lands, `search` already equals
  // `debouncedSearch`, so this is a no-op then, not a fight with the in-progress keystroke.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    // Guards against a race with a direct/immediate clear (handleRemoveSearch, handleClearAll):
    // right after one of those resets `searchInput`, `debouncedSearch` is still the pre-clear
    // value for up to SEARCH_DEBOUNCE_MS — without this check, this effect would see it differ
    // from the now-cleared URL and write the stale value straight back in. Only commit once the
    // debounce has genuinely caught up to the current live input, not merely whenever it
    // disagrees with the URL.
    if (debouncedSearch !== searchInput) return;
    if (debouncedSearch === search) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (debouncedSearch) next.set(SEARCH_PARAM, debouncedSearch);
        else next.delete(SEARCH_PARAM);
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, searchInput, search, setSearchParams]);

  const facetInput = useMemo(() => ({ ...filters, search: search || undefined }), [filters, search]);

  const {
    data: recipesData,
    isPending: recipesPending,
    isError,
    error,
    refetch,
  } = useRecipes({
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    ...facetInput,
    bookmarkedOnly: savedOnly || undefined,
  });

  const { data: facetGroups } = useRecipeFacets(facetInput);

  const { toggle: toggleBookmark } = useToggleRecipeBookmark();

  // Always all 9 groups, even before the first response arrives (empty `options` until then) —
  // `ContentFilters`' accordion snapshots its open section at first mount (uncontrolled), so an
  // initial `facets = []` would permanently leave every section collapsed once data does arrive.
  const facets: Facet[] = useMemo(() => {
    const byVocabulary = new Map((facetGroups ?? []).map((group) => [group.vocabulary, group]));
    return RECIPE_FACET_ORDER.map((vocabulary) => {
      const meta = RECIPE_FACET_GROUPS[vocabulary];
      const group = byVocabulary.get(vocabulary);
      return {
        id: meta.fieldId,
        label: t(`recipes.filters.categories.${meta.fieldId}`),
        options: (group?.values ?? []).map((value) => ({ value: value.term, label: value.term, count: value.count })),
      };
    });
  }, [facetGroups, t]);

  // An explicit pager click pushes a new history entry, so browser back/forward step through
  // previously-viewed pages. ContentListing's own stale-page clamp passes { replace: true } for
  // its correction instead — a push there would let Back restore the stale page and immediately
  // re-trigger the same correction, trapping the back button.
  const handlePageChange = (nextPage: number, options?: { replace?: boolean }) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(PAGE_PARAM, String(nextPage));
        return next;
      },
      { replace: options?.replace ?? false },
    );
  };

  // Filter changes are frequent, granular refinements (unlike an explicit page click), so they
  // replace the current history entry rather than pushing a new one for every checkbox toggle.
  const handleFacetChange = (facetId: string, values: string[]) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(facetId);
        for (const value of values) next.append(facetId, value);
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const handleRemoveChip = (facetId: string, term: string) => {
    handleFacetChange(
      facetId,
      (filters[facetId] ?? []).filter((value) => value !== term),
    );
  };

  // Same reset/replace treatment as the other filters (handleFacetChange): resets to page 1,
  // replaces rather than pushes a history entry.
  const handleToggleSaved = (next: boolean) => {
    setSearchParams(
      (current) => {
        const nextParams = new URLSearchParams(current);
        if (next) nextParams.set(SAVED_PARAM, '1');
        else nextParams.delete(SAVED_PARAM);
        nextParams.delete(PAGE_PARAM);
        return nextParams;
      },
      { replace: true },
    );
  };

  const handleRemoveSearch = () => {
    setSearchInput('');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(SEARCH_PARAM);
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const handleClearAll = () => {
    setSearchInput('');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const fieldId of FIELD_IDS) next.delete(fieldId);
        next.delete(SEARCH_PARAM);
        next.delete(SAVED_PARAM);
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  // Sourced from `filters` (the URL), not `facets`, so a deep-linked or not-yet-loaded selection
  // still renders a chip immediately — falling back to the raw term until the option label (same
  // string, from the facets query) arrives. Cheap enough to recompute every render; not worth
  // memoizing against a stable-callback dependency that doesn't exist.
  const labelByFacet = new Map(
    facets.map((facet) => [facet.id, new Map(facet.options.map((o) => [o.value, o.label]))]),
  );
  const chips: ContentListingChip[] = FIELD_IDS.flatMap((fieldId) =>
    (filters[fieldId] ?? []).map((term) => ({
      key: `${fieldId}-${term}`,
      label: labelByFacet.get(fieldId)?.get(term) ?? term,
      onRemove: () => handleRemoveChip(fieldId, term),
    })),
  );

  const extraChips: ContentListingChip[] = search
    ? [{ key: 'search', icon: 'search', label: `"${search}"`, onRemove: handleRemoveSearch }]
    : [];

  const hasActiveFilters = savedOnly || Boolean(search) || Object.values(filters).some((values) => values.length > 0);

  // Strict equality, not `?? 0 === 0`: before the first response arrives savedCount is undefined,
  // and that must not read as "zero saved recipes" and flash the saved-empty state early.
  const savedEmpty = savedOnly && recipesData?.savedCount === 0;
  const savedCount = recipesData?.savedCount ?? 0;
  const savedRecipesLabel = t('recipes.filters.savedRecipes', { count: savedCount });

  // `isPending`, not `isFetching`: `keepPreviousData` (see useRecipes.ts) means the grid already
  // has cards to show during a background refetch (a debounced search commit, a facet toggle, a
  // page change) — `isPending` is only true before any data has ever loaded for this page. Not
  // coupled to the facets query at all: a facet-count refetch shouldn't blank the results grid.
  const loading = recipesPending;

  return (
    <div className="flex flex-col gap-6">
      <BackLink href="/resources/nutrition" label={t('recipes.backToNutrition')} />
      {isError ? (
        <Alert variant="destructive">
          <Icon name="alertCircle" />
          <AlertTitle>{tError('title')}</AlertTitle>
          <AlertDescription>
            <p>{tError(extractGqlErrorCode(error))}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              {t('errors.tryAgain')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <ContentListing
          noun={t('recipes.noun')}
          nounSingular={t('recipes.nounSingular')}
          title={t('recipes.title')}
          searchLabel={t('recipes.searchPlaceholder')}
          q={searchInput}
          onQChange={setSearchInput}
          facets={facets}
          value={filters}
          onFacetChange={handleFacetChange}
          cards={(recipesData?.edges ?? []).map((recipe) => ({
            ...toRecipeCard(recipe),
            onSaveChange: (next: boolean) => toggleBookmark({ entityId: recipe.id, title: recipe.title, next }),
          }))}
          filteredCount={recipesData?.totalCount ?? 0}
          loading={loading}
          emptyHint={
            savedEmpty
              ? t('recipes.empty.saved.hint')
              : hasActiveFilters
                ? t('recipes.emptyFilteredHint')
                : t('recipes.emptyMessage')
          }
          emptyTitle={
            savedEmpty ? t('recipes.empty.saved.title') : hasActiveFilters ? t('recipes.emptyFilteredTitle') : undefined
          }
          emptyActionLabel={!savedEmpty && hasActiveFilters ? t('recipes.emptyFilteredAction') : undefined}
          onEmptyAction={!savedEmpty && hasActiveFilters ? handleClearAll : undefined}
          pageSize={PAGE_SIZE}
          page={page}
          onPageChange={handlePageChange}
          primaryControls={
            <div className="flex min-h-11 items-center gap-3">
              <Checkbox
                id="recipes-saved-rail"
                checked={savedOnly}
                onCheckedChange={(next) => handleToggleSaved(!!next)}
              />
              <Label htmlFor="recipes-saved-rail" className="whitespace-nowrap text-sm font-normal">
                {savedRecipesLabel}
              </Label>
            </div>
          }
          drawerLeading={
            <fieldset className="border-b py-4">
              <legend className="pb-2 text-sm font-semibold text-foreground">
                {t('recipes.filters.savedRecipes', { count: 0 })}
              </legend>
              <div className="flex min-h-11 items-center gap-2">
                <Switch
                  id="recipes-saved-drawer"
                  checked={savedOnly}
                  onCheckedChange={(next) => handleToggleSaved(!!next)}
                />
                <Label htmlFor="recipes-saved-drawer" className="whitespace-nowrap text-sm font-normal">
                  {savedRecipesLabel}
                </Label>
              </div>
            </fieldset>
          }
          filterCountExtra={savedOnly ? 1 : 0}
          chips={chips}
          extraChips={extraChips}
          onClearAll={handleClearAll}
        />
      )}
    </div>
  );
}
